import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import PageLoader from '../ui-library/PageLoader';
import { useAuth } from './auth/AuthProvider';
import useGuestAddress, { isCountyPreview } from '@/hooks/useGuestAddress';
import GuestLandingPage from './landing/GuestLandingPage';

/**
 * Root-route landing. Branches on auth + address state AND viewport:
 *
 *   - Signed-in users → redirect to /chat. They've already provided
 *     their address during onboarding, so the address-prompt landing
 *     would be redundant.
 *   - Guests with a saved address → redirect to /chat. They've
 *     already completed the landing's call-to-action; the landing
 *     would be a step backwards. Catches the "back button after See
 *     my ballot" case explicitly. A county preview doesn't count as a
 *     saved address; those guests still get the landing on mobile.
 *   - Guests with no address on desktop (md+) → redirect to /chat.
 *     The chat view's right pane embeds the same landing UI in that
 *     state, so ``/`` and ``/chat`` are functionally identical on
 *     desktop — no need for a separate full-page landing.
 *   - Guests with no address on mobile (xs) → render the standalone
 *     guest landing page. The mobile chat experience doesn't have
 *     room for an embedded landing, so the full-screen address-
 *     prompt landing stays as the dedicated entry point.
 *
 * While Auth0 is still initializing we show a loader rather than
 * flashing the guest landing — otherwise an authenticated user would
 * briefly see the landing before getting redirected.
 */
const LandingPageRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isInitializing } = useAuth();
  const { address: guestAddress } = useGuestAddress();
  // A county preview is a guess, not a confirmed address: the voter still
  // needs the landing's prompt for their registered address.
  const hasGuestAddress =
    Boolean(guestAddress?.formatted_address) && !isCountyPreview(guestAddress);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const shouldRedirectToChat = isAuthenticated || hasGuestAddress || isDesktop;

  useEffect(() => {
    if (isInitializing) return;
    // Carry the current query string across the hop to /chat so params like
    // ?address= survive — a bare navigate('/chat') would drop them and break
    // the address pre-fill on the desktop right-pane embed.
    if (shouldRedirectToChat) {
      navigate({ pathname: '/chat', search: location.search }, { replace: true });
    }
  }, [isInitializing, shouldRedirectToChat, navigate, location.search]);

  if (isInitializing || shouldRedirectToChat) return <PageLoader />;
  return <GuestLandingPage />;
};

export default LandingPageRedirect;
